<template>
  <div class="compare-rank-zone-modal">
    <div class="compare-rank-zone-modal__header">
      <div class="compare-rank-zone-modal__title" v-html="title"></div>
      <div v-if="!dataMissing" @click="exportCsv" class="compare-rank-zone-modal__export-button">
        <base-material-design-icon icon="download" />
      </div>
      <div class="compare-rank-zone-modal__info">
        <span>MSV: {{ msvCount | roundNumber | commaDividedNumber }}</span>
        <span>Keywords: {{ totalItems | commaDividedNumber }}</span>
      </div>
    </div>

    <div v-if="dataMissing" class="compare-rank-zone-modal__no-data">No records found</div>

    <div v-else-if="isDataFetched" class="compare-rank-zone-modal__table">
      <div class="compare-rank-zone-modal__table_row compare-rank-zone-modal__table_header-row">
        <div v-for="column in tableConfig"
             :key="column.propName"
             @click.exact="onHeaderCellClicked(column)"
             class="compare-rank-zone-modal__table_header-cell compare-rank-zone-modal__table_cell"
             :class="[`compare-rank-zone-modal__${column.propName}`]">
          <base-table-sortable-column
            :label="column.label"
            :prop-name="column.propName"
            :sort-config="sortConfig"/>
        </div>
      </div>

      <div v-for="data in filteredCompareRankZone"
           :key="data.keyword"
           class="compare-rank-zone-modal__table_row">
        <div v-for="column in tableConfig"
             :key="column.propName"
             class="compare-rank-zone-modal__table_cell"
             :class="[`compare-rank-zone-modal__${column.propName}`]">
          <template v-if="column.propName === 'msv'">
            {{ data.msv | roundNumber | commaDividedNumber }}
          </template>
          <template v-else-if="column.propName === 'score'">
            {{ data.score | commaDividedNumber }}
          </template>
          <div v-else-if="column.propName === 'keyword'">
            <base-search-in-google :query="data.keyword"/>
            <span @click.prevent.stop="onKeywordClick(data.keyword)">{{ data.keyword }}</span>
          </div>
          <template v-else-if="column.propName.endsWith('_rank')">
            {{ data[column.propName] | rankFilter }}
          </template>
          <div v-else-if="column.propName === 'difference'" v-html="getDifference(data.difference)">
          </div>
        </div>
      </div>
      <div v-if="hasPagination" class="compare-rank-zone-modal__table_row">
        <base-pagination class="compare-rank-zone-modal__table_pagination"
                         :totalItems="totalItems"
                         :limit="10"
                         @pageChanged="onPageChange"/>
      </div>
    </div>

    <div class="compare-rank-zone-modal__loader-container" v-else>
      <div class="compare-rank-zone-modal__loader">
        <base-loader/>
      </div>
    </div>

    <button class="compare-rank-zone-modal__button compare-rank-zone-modal__button_close"
            @click.prevent="close">
      Close
    </button>
    <modal :name="keywordMovementModalName"
           v-bind="modalProps"
    >
      <base-keyword-history-modal
        :predefinedWebPropertyGroupId="clientWebPropertyId"
        :comparison="webProperty === clientWebPropertyId ? [] : [webProperty]"
        :keyword="selectedKeyword"
        @close="closeModal"
      />
    </modal>
  </div>
</template>

<script>
import moment from 'moment'
import _ from 'lodash'
import filters from '@/common/Filters'
import { mapActions, mapState } from 'vuex'
import BaseModalMixins from '@/common/BaseModalMixins'
import BaseSortMixins from '@/common/BaseSortMixins'
import helper from '@/services/Helper'
import baseModalProps from '@/common/BaseModalProps'
import csvExport from '@/services/CSVExport'

const ITEMS_PER_PAGE = 10
export default {
  name: 'CompareRankZoneModal',
  mixins: [BaseModalMixins, BaseSortMixins],
  mounted () {
    this.loadData()
  },
  beforeDestroy () {
    this.clearCompareRankZoneData()
  },
  props: {
    queryData: {
      type: Object,
      default: () => ({})
    },
    clientWebPropertyName: {
      type: String,
      default: ''
    },
    clientWebPropertyId: {
      type: String,
      default: ''
    }
  },
  data () {
    return {
      isDataFetched: false,
      sortConfig: [{
        sortDirection: helper.DIRECTION_ASC,
        propertyName: 'client_rank'
      }],
      paginationProps: {
        from: 0,
        to: ITEMS_PER_PAGE
      },
      selectedKeyword: '',
      // uses to avoid open modal twice in component duplicates
      keywordMovementModalName: Math.random().toString(36).substring(7)
    }
  },
  computed: {
    ...mapState('rankZoneCompetition', {
      filteredCompareRankZone (state) {
        const { from, to } = this.paginationProps
        return helper.sortByMultipleParams(_.cloneDeep(state.compareRankZone), this.sortConfig).slice(from, to)
      },
      totalItems (state) {
        return state.compareRankZone.length
      },
      compareRankZone: 'compareRankZone'
    }),
    hasPagination () {
      return this.totalItems > ITEMS_PER_PAGE
    },
    isClientView () {
      return this.clientWebPropertyName === this.queryData.webPropertyName
    },
    webProperty () {
      return this.queryData['web_property']
    },
    tableConfig () {
      const tableConfig = [
        {
          label: 'Keyword',
          propName: 'keyword'
        },
        {
          label: 'MSV',
          propName: 'msv'
        },
        {
          label: 'Score',
          propName: 'score'
        }
      ]
      if (!this.isClientView) {
        // const label = this.$options.filters.truncateLabel(this.queryData.webPropertyName)
        tableConfig.push({ label: `Ranking<br>${this.queryData.webPropertyName}`, propName: 'competitor_rank' })
      }

      // const label = this.$options.filters.truncateLabel(this.clientWebPropertyName)
      tableConfig.push({ label: `Ranking<br>${this.clientWebPropertyName}`, propName: 'client_rank' })

      if (!this.isClientView) {
        tableConfig.push({ label: 'Difference', propName: 'difference' })
      }

      return tableConfig
    },
    title () {
      return `<span class="compare-rank-zone-modal__title_web-property">${(this.isClientView ? '' : 'Competitor ') + this.queryData.webPropertyName}</span>` + `<span class="compare-rank-zone-modal__title_date">${moment(this.queryData.date).format('MMMM DD YYYY')}</span>` + this.queryData['rank_zone']
    },
    dataMissing () {
      return !this.totalItems && this.isDataFetched
    },
    msvCount () {
      return _.sumBy(this.compareRankZone, 'msv')
    },
    modalProps () {
      const shareModalProps = {
        width: '75%'
      }
      return { ...baseModalProps, ...shareModalProps }
    }
  },
  methods: {
    ...mapActions('rankZoneCompetition', ['getCompareRankZoneData', 'clearCompareRankZoneData']),
    async loadData () {
      await this.getCompareRankZoneData({ clientId: this.clientId, ...this.queryData })
      this.isDataFetched = true
    },
    close () {
      this.$emit('close')
    },
    onKeywordClick (keyword) {
      this.selectedKeyword = keyword
      this.$modal.show(this.keywordMovementModalName)
    },
    closeModal () {
      this.selectedKeyword = ''
      this.$modal.hide(this.keywordMovementModalName)
    },
    onPageChange ({ from = 0, to = 10 }) {
      this.paginationProps = { from, to }
    },
    getDifference (difference) {
      if (!difference || ['NR', 'ND'].includes(difference)) {
        return `<span style="color: #7a8894">${difference}</span>`
      } else {
        return `<span style="color: ${difference > 0 ? '#008041' : '#e11c21'}">${difference > 0 ? difference : difference * (-1)}</span>`
      }
    },
    exportCsv () {
      const getRankZone = (rank) => {
        switch (true) {
          case rank <= 3:
            return '01 to 03'
          case rank <= 7:
            return '04 to 07'
          case rank <= 10:
            return '08 to 10'
          case rank <= 25:
            return '11 to 25'
          case rank <= 50:
            return '26 to 50'
          case rank <= 100 && rank !== 404:
            return '51 to 100'
          case rank === 404:
            return 'ND'
          default:
            return 'NR'
        }
      }
      const handler = (columns) => {
        return item => {
          const row = []
          columns.forEach(name => {
            if (name === 'rank_zone') {
              row.push(`"${getRankZone(item.client_rank)}"`)
            } else if (name === 'content_brief_id') {
              row.push(`"${window.location.origin}/dashboard#/content-briefs/${item.content_brief_id}/${item.is_published ? 'progress' : 'strategy'}"`)
            } else if (['client_rank', 'competitor_rank'].includes(name)) {
              row.push(`"${rankFilter(item[name])}"`)
            } else if (name === 'client_url') {
              row.push(`"${item.client_rank <= 100 ? item.client_url : rankFilter(item.client_rank)}"`)
            } else if (name === 'competitor_url') {
              row.push(`"${item.competitor_url <= 100 ? item.competitor_url : rankFilter(item.competitor_url)}"`)
            } else if (name === 'snapshot_date') {
              row.push(`"${moment(item.snapshot_date).format('DD/MM/YYYY')}"`)
            } else {
              row.push(`"${item[name]}"`)
            }
          })
          data.push(row)
        }
      }

      const rankFilter = this.$options.filters.rankFilter

      const clientName = this.clientWebPropertyName
      let header = ['"Keyword"', '"Relevancy Score"', '"Monthly Searches"', '"Rank Date"', '"Rank Zone"',
        `"Rank: ${clientName}"`, `"Ranking URL: ${clientName}"`, '"Mapped URL"', '"Mapped URL Content Brief"']
      let columnNames = ['keyword', 'score', 'msv', 'snapshot_date', 'rank_zone',
        'client_rank', 'client_url', 'mapped_url', 'content_brief_id']

      if (!this.isClientView) {
        const competitorName = this.queryData.webPropertyName
        header = ['"Keyword"', '"Relevancy Score"', '"Monthly Searches"', '"Rank Date"', '"Rank Zone"',
          `"Rank: ${clientName}"`, `"Ranking URL: ${clientName}"`, `"Rank: ${competitorName}"`, `"Ranking URL: ${competitorName}"`,
          '"Mapped URL"', '"Mapped URL Content Brief"']
        columnNames = ['keyword', 'score', 'msv', 'snapshot_date', 'rank_zone',
          'client_rank', 'client_url', 'competitor_rank', 'competitor_url', 'mapped_url', 'content_brief_id']
      }

      const data = []
      data.push(header)
      // sort by rank
      _.cloneDeep(this.compareRankZone).forEach(handler(columnNames))
      csvExport(`${this.queryData.webPropertyName}_${moment(this.queryData.date).format('MMMM_DD_YYYY')}_${this.queryData['rank_zone']}`, data)
    }
  },
  filters: {
    roundNumber: filters.roundNumber,
    commaDividedNumber: filters.commaDividedNumber,
    rankFilter: (v) => v === 404 ? 'ND' : v >= 101 ? 'NR' : v,
    truncateLabel: filters.truncateLabel
  }
}
</script>

<style lang="scss">
.compare-rank-zone-modal {
  display: flex;
  flex-direction: column;
  overflow: hidden;

  &__keyword {
    flex: 1;

    & > div {
      display: inline-block;

      & > div {
        padding-left: 3px !important;
      }

      & > span {
        padding-left: 29px;

        &:hover {
          text-decoration: underline;
          cursor: pointer;
          color: $rfx-bright-blue;
        }
      }
    }
  }

  &__msv,
  &__score {
    flex: 0 0 84px;
  }

  &__competitor_rank,
  &__client_rank {
    flex: 0 0 200px;
    & > span {
      white-space: nowrap;
    }
  }

  &__difference {
    flex: 0 0 88px;
  }

  &__table {
    margin-top: 24px;
    display: flex;
    flex-direction: column;
    width: -webkit-fill-available;
    overflow: hidden;

    &_pagination {
      margin: 12px auto 12px;
    }

    &_header-row {
      background: $rfx-quarter-march;
    }

    &_row {
      width: -webkit-fill-available;
      display: flex;
      align-items: center;
      border-bottom: 1px solid rgba(0,0,0,.05);
    }

    &_cell {
      color: $rfx-dark-march;
      padding: 22px 0;

      &:first-child {
        padding-left: 40px;
      }

      &:last-child {
        padding-right: 40px;
      }
    }

    &_header-cell {
      background-color: $rfx-quarter-march;
      color: #fff;
      border-color: $rfx-quarter-march;
      cursor: pointer;

      .common-table__sort-arrow {
        color: #fff;
      }
    }
  }

  &__header {
  }

  &__info {
    display: flex;
    padding: 0 40px 0;

    span {
      color: $rfx-dark-march;
      font-size: 14px;
      line-height: 16px;

      &:first-child {
        margin-right: 28px;
      }
    }
  }

  &__title {
    padding: 40px 40px 16px;
    text-align: left;
    font-size: 24px;
    line-height: 24px;
    color: $rfx-dark-march;

    &_date {
      padding: 0 40px;
    }

    &_web-property {
      font-weight: bold;
    }
  }

  &__export-button {
    position: absolute;
    top: 44px;
    right: 35px;
    font-size: 18px;
    color: $rfx-bright-blue;
  }

  &__button {
    width: 100px;
    height: 40px;
    border-radius: 4px;
    font-size: 10px;
    font-weight: 900;
    text-transform: uppercase;
    border: none;
    outline: none;
    align-self: flex-end;
    margin: 20px;
    cursor: pointer;

    &_close {
      background-color: $rfx-medium-march;
      color: #fff;
    }
  }

  &__loader {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 200px;

    &-container {
    }
  }

  &__no-data {
    display: flex;
    flex: 0 0 200px;
    background-color: #fff;
    border-radius: $rfx-default-border-radius;
    align-items: center;
    justify-content: center;
    font-size: 27px;
    font-weight: 600;
    color: $rfx-medium-grey;
  }
}
</style>
